// Workspace switcher: the brand block at the top of the sidebar. Shows the
// active workspace and opens a list of the user's workspaces plus "New
// workspace". Switching asks the API for a token for the other workspace and
// reloads the app with it (see switchToToken).

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError, authApi, workspacesApi } from '../lib/api-client'
import { switchToToken, useWorkspaceId } from '../lib/auth'
import { humanize } from '../lib/format'
import { LogoMark } from './logo'
import { CreateWorkspaceDialog } from './create-workspace-dialog'

export function WorkspaceSwitcher() {
  const workspaceId = useWorkspaceId()
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: workspacesApi.listMine,
    enabled: workspaceId !== '',
    staleTime: 30_000,
  })
  const workspaces = workspacesQuery.data ?? []
  const active = workspaces.find((w) => w.id === workspaceId)

  const switchMutation = useMutation({
    mutationFn: authApi.switchWorkspace,
    onSuccess: (result) => switchToToken(result.token, '/'),
  })

  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false)
        // The list unmounts with focus inside it; hand focus back to the trigger.
        triggerRef.current?.focus()
      }
    }
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  function choose(id: string): void {
    if (id === workspaceId) {
      setOpen(false)
      triggerRef.current?.focus()
      return
    }
    switchMutation.mutate(id)
  }

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        className="-mx-2 -my-1.5 flex w-[calc(100%+1rem)] min-w-0 items-center gap-2.5 rounded-control px-2 py-1.5 text-left hover:bg-sunken"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Switch workspace, current: ${active?.name ?? 'Workspace'}`}
        onClick={() => setOpen((v) => !v)}
      >
        <LogoMark size={32} />
        <span className="min-w-0 flex-1">
          <span className="block font-display text-lg font-semibold leading-tight tracking-[-0.01em] text-ink">
            Goblic
          </span>
          <span className="block truncate text-sm leading-tight text-ink-3">
            {active?.name ?? 'Workspace'}
          </span>
        </span>
        <span aria-hidden className="text-ink-3">
          ▾
        </span>
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-40 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-panel border border-line bg-surface p-2 shadow-overlay">
          <p className="px-2 pb-1 text-sm text-ink-3">Workspaces</p>
          <ul aria-label="Workspaces" className="max-h-72 overflow-y-auto">
            {workspaces.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  disabled={switchMutation.isPending}
                  aria-current={w.id === workspaceId ? 'true' : undefined}
                  onClick={() => choose(w.id)}
                  className={`flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left hover:bg-sunken ${
                    w.id === workspaceId ? 'text-moss-strong' : 'text-ink'
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base">{w.name}</span>
                    <span className="block truncate text-sm text-ink-3">
                      {w.roles.map(humanize).join(', ')}
                    </span>
                  </span>
                  {w.id === workspaceId ? <span aria-hidden>✓</span> : null}
                </button>
              </li>
            ))}
          </ul>
          {workspacesQuery.isError ? (
            <p className="px-2 py-2 text-sm text-ink-3">Couldn’t load your workspaces.</p>
          ) : null}
          {switchMutation.isError ? (
            <p className="px-2 py-2 text-sm text-danger" role="alert">
              {switchMutation.error instanceof ApiError
                ? switchMutation.error.message
                : 'Couldn’t switch workspace.'}
            </p>
          ) : null}
          <div className="mt-2 border-t border-line pt-2">
            <button
              type="button"
              className="block w-full rounded-control px-2 py-1.5 text-left text-base text-ink-2 hover:bg-sunken hover:text-ink"
              onClick={() => {
                setOpen(false)
                setCreating(true)
              }}
            >
              New workspace
            </button>
          </div>
        </div>
      ) : null}
      <CreateWorkspaceDialog
        open={creating}
        onClose={() => {
          setCreating(false)
          // The "New workspace" button unmounted when the list closed, so the dialog can't restore focus to it.
          triggerRef.current?.focus()
        }}
      />
    </div>
  )
}
